import colors from 'lib/constants/resourceColors';
import Privileges from 'lib/constants/privileges';
import { invoiceSearchFields, invoiceSearchColumns, invoiceReportLinks } from './invoices';

// Per-type Invoices sub-views (Contract / Project / Service). Each reuses the
// same `Invoice` backend resource (dataUrl stays /invoices) but pins a single
// invoice type via defaultFilter, so the list only shows that type. Rows still
// open the canonical invoice view at /invoices/:id.
//
// These are registered as explicit routes in routes/routes.js (NOT via the
// resources index) because their appUrl (/invoices/<type>) would otherwise be
// captured by the generic /invoices/:id read route — the same reason the
// /contracts/upcoming_invoices route is declared explicitly.

// Download CSV link builder: exports only this type, honors the Invoice Date
// filter, and (server-side) never includes voided invoices.
const downloadLink = (type) => ({
    downloadURL: (search_state) => {
        const queries = [`type=${type}`];
        if (search_state.search_by_date) {
            if (search_state.date_range_type === 'custom') {
                if (search_state.start_date) queries.push(`start_date=${search_state.start_date}`);
                if (search_state.end_date) queries.push(`end_date=${search_state.end_date}`);
            } else if (search_state.date_range_type && search_state.date_range_type !== 'all') {
                queries.push(`date_range_type=${search_state.date_range_type}`);
            }
        }
        return `/files/invoices_list?${queries.join('&')}`;
    },
    title: 'Download Sales Register',
    requiredPrivileges: [Privileges.FILE_READ_INVOICES_LIST],
});

// Contract and Service Invoices drop the Project and Salesperson columns (not
// relevant for that billing). Project and legacy /invoices keep the full set.
const noProjectSalespersonColumns = invoiceSearchColumns.filter(
    (col) => col.accessor !== 'project_description' && col.accessor !== 'salesperson_name'
);

function makeInvoiceTypeView({ type, heading, appUrlBase, cacheKey, columns = invoiceSearchColumns }) {
    return {
        defaults: {
            name: 'Invoice',
            color: colors.invoices,
            appUrlBase,
            // search-only: view/create/delete stay on the canonical Invoices resource.
            actions: ['search'],
        },
        search: {
            heading,
            cacheKey,
            // Pin the invoice type for every query on this screen, and hide
            // voided invoices by default.
            defaultFilter: () => ({ type, voided: false }),
            allowDateFilter: true,
            // Only the Invoice Date field is offered (with all the usual range
            // presets + custom range from the Search component).
            dateFieldOptions: [{ label: 'Invoice Date', value: 'date', description: 'invoiced' }],
            fields: invoiceSearchFields,
            // A single "Show Void" checkbox instead of the old All/Void radio.
            // Unchecked (default) keeps the defaultFilter's `voided: false`, so
            // only live invoices of this type show. Checked overrides `voided`
            // with null, which `api.getQueryParams` drops entirely, so the query
            // returns both live and voided invoices of this type. Downloads are
            // unaffected: the report endpoints force voided=false server-side.
            toggleOptions: [
                { key: 'show_void', label: 'Show Void', param: 'voided', value: () => null },
            ],
            // Four download buttons: this view's type-scoped CSV plus the three
            // legacy accounting reports (Received Payments, Sales Tax, Sales Tax
            // Error) restored from the old /invoices screen. The legacy three are
            // global (not type-scoped) and honor the Invoice Date filter.
            links: [downloadLink(type), ...invoiceReportLinks(type)],
            searchBy: ['invoice number', 'project name', 'customer name', 'grand total'],
            tableProps: {
                rowURL: '/invoices/:id',
                columns,
            },
        },
    };
}

export const ContractInvoices = makeInvoiceTypeView({
    type: 'C', heading: 'Contract Invoices',
    appUrlBase: '/invoices/contract', cacheKey: 'cached_search_contract_invoices',
    columns: noProjectSalespersonColumns,
});

export const ProjectInvoices = makeInvoiceTypeView({
    type: 'P', heading: 'Project Invoices',
    appUrlBase: '/invoices/project', cacheKey: 'cached_search_project_invoices',
});

export const ServiceInvoices = makeInvoiceTypeView({
    type: 'S', heading: 'Service Invoices',
    appUrlBase: '/invoices/service', cacheKey: 'cached_search_service_invoices',
    columns: noProjectSalespersonColumns,
});
